import { IProductCard } from '@src/components/product-card/product-card.types'
import { MODULES, FEATURES } from '@src/quince-site-common/src/constants/template-info'
import wishListStateManager from '@src/quince-site-common/src/redux/globalStateManager/WishlistStateManager'
import productService from '@src/quince-site-common/src/services/productService'
import { IWishlistItem } from '@src/quince-site-common/src/services/Wishlist.types'
import wishlistService from '@src/quince-site-common/src/services/wishlistService'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { SharedWishListDataType, WishlistItem } from './wishlist.types'

// NOTE: SVD wishlist hydration lives in ./svd/svd-wishlist.helpers.ts — this file is the legacy
// (non-SVD) path only, unchanged in behaviour.

export const fetchUserWishListProducts = async (subscriptionExpEnabled: boolean): Promise<WishlistItem[]> => {
  try {
    const wishlistItems: IWishlistItem[] = AuthUtil.isLoggedIn()
      ? await wishlistService.wishListAlreadyLoggedIn(AuthUtil.getUser().email ?? '')
      : wishlistService.fetchUserWishListFromLocalStorage()
    wishListStateManager.setWishlistTotalItemsCount(wishlistItems.length)

    const productCards = await productService.getProductCardsBatched(
      {
        spids: wishlistItems.map(item => item.product_id),
        colors: wishlistItems.map(item => item.color),
      },
      subscriptionExpEnabled,
    )

    return await pruneStaleColorWishlistEntries(productCards, wishlistItems)
  } catch (error) {
    captureException(error, {
      module: MODULES.WISHLIST,
      feature: FEATURES.FETCH_WISHLIST_PRODUCTS,
    })
    return []
  }
}

export const fetchSharedWishList = async (
  id: string,
  subscriptionExpEnabled: boolean,
): Promise<SharedWishListDataType> => {
  // NOTE: The shared-wishlist render path is subject to the same retired-color
  // pairing failure as `fetchUserWishListProducts` but cannot be auto-fixed —
  // the viewer doesn't own the entries, so removeFromWishlist would mutate the
  // wrong wishlist. Tracked as follow-up.
  const res = await wishlistService.getSharedWishlist(id)
  const wishListItems = res.wishlist ?? []

  const ownWishListItems = AuthUtil.isLoggedIn()
    ? await wishlistService.wishListAlreadyLoggedIn(AuthUtil.getUser().email ?? '')
    : wishlistService.fetchUserWishListFromLocalStorage()

  wishListStateManager.setWishlistTotalItemsCount(ownWishListItems.length)

  const productList = await productService.getProductCardsBatched(
    {
      spids: wishListItems.map(item => item.product_id),
      colors: wishListItems.map(item => item.color),
    },
    subscriptionExpEnabled,
  )
  return { wishListItems: productList, name: res.user_name }
}

// Drops wishlist entries whose stored color is no longer offered by the product
// and removes them from the user's wishlist. Without this, the card falls back
// to a different available color, the heart and delete button both key off
// that wrong color, and the user is left unable to clear the stale entry.
async function pruneStaleColorWishlistEntries(
  productCards: IProductCard[],
  wishlistItems: IWishlistItem[],
): Promise<IProductCard[]> {
  if (productCards.length === 0 || wishlistItems.length === 0) {
    return productCards
  }

  const itemsBySpid = new Map<string, IWishlistItem[]>()
  for (const item of wishlistItems) {
    const key = String(item.product_id)
    const list = itemsBySpid.get(key) ?? []
    list.push(item)
    itemsBySpid.set(key, list)
  }

  const staleItemsToPurge: IWishlistItem[] = []
  const freshCards: IProductCard[] = []

  for (const card of productCards) {
    const spid = String(card.product.relatedIds.spid)
    const candidates = itemsBySpid.get(spid)
    if (!isNonEmpty(candidates)) {
      freshCards.push(card)
      continue
    }

    const variants = card.product.cardVariants
    if (!isNonEmpty(variants)) {
      // Empty cardVariants likely means a partial/flaky BFF response — never
      // purge on missing data.
      itemsBySpid.set(spid, candidates.slice(1))
      freshCards.push(card)
      continue
    }

    // Pairing relies on `getProductCardsBatched` returning one card per
    // (spid, color) input pair in input order — true today via flatMap over
    // settled batches. If the BFF starts deduping or reordering, this loop
    // will need to switch to identity-based pairing.
    //
    // For multiple wishlist entries on the same spid (different colors), pair
    // each card with the candidate whose color was put first by BFF reorder.
    // Single-entry case naturally falls back to candidates[0].
    const firstVariantColor = variants[0].displayConfig.value
    const candidate = candidates.find(c => c.color === firstVariantColor) ?? candidates[0]
    itemsBySpid.set(
      spid,
      candidates.filter(c => c !== candidate),
    )

    const colorExists = variants.some(v => v.displayConfig.value === candidate.color)
    if (colorExists) {
      freshCards.push(card)
    } else {
      staleItemsToPurge.push(candidate)
    }
  }

  if (staleItemsToPurge.length > 0) {
    // Best-effort: never fail the wishlist load on a backend remove error,
    // but report rejections so we can alert on backend regressions.
    const settled = await Promise.allSettled(
      staleItemsToPurge.map(item => wishlistService.removeFromWishlist(String(item.product_id), item.color)),
    )
    settled.forEach((result, i) => {
      if (result.status === 'rejected') {
        captureException(result.reason, {
          module: MODULES.WISHLIST,
          feature: FEATURES.FETCH_WISHLIST_PRODUCTS,
          extraInfo: { staleItem: staleItemsToPurge[i] },
        })
      }
    })
    wishListStateManager.setWishlistTotalItemsCount(wishlistItems.length - staleItemsToPurge.length)
  }

  return freshCards
}

function isNonEmpty<T>(arr: T[] | undefined): arr is [T, ...T[]] {
  return arr !== undefined && arr.length > 0
}
